import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { CloseIcon } from './Icons';

const APP_STORE_URL = 'https://apps.apple.com/kw/app/ba7ria-%D8%A8%D8%AD%D8%B1%D9%8A%D8%A9/id6792278900';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=ba7ria.com';
const DISMISS_KEY = 'ba7ria_download_banner_dismissed';

// Store badges always render their eyebrow/title in Latin text regardless of
// page language — matches how the real Apple/Google badges behave.
const BADGE_FONT = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif';

const AppleGlyph = () => (
  <svg viewBox="0 0 384 512" width="15" height="18" fill="white" aria-hidden="true">
    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
  </svg>
);

const PlayGlyph = () => (
  <svg viewBox="0 0 24 24" width="14" height="16" fill="white" aria-hidden="true">
    <path d="M5 3l14 9-14 9V3z" />
  </svg>
);

const PhoneGlyph = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="white" strokeWidth="1.8" aria-hidden="true">
    <rect x="7" y="2" width="10" height="20" rx="2.2" />
    <path d="M11 18h2" strokeLinecap="round" />
  </svg>
);

// Same black-pill badge shape for both stores, styled after Apple/Google's
// own official monochrome badge variants (Apple's dark badge, Google's
// outline/mono "Get it on Google Play" badge) rather than the full-color
// ones — keeps both consistent with each other and with this banner's
// solid-brown background.
const StoreBadge = ({ href, icon, eyebrow, title }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="flex-1 flex items-center justify-center gap-1.5 bg-black hover:bg-gray-900 transition-colors rounded-lg px-2.5 py-1.5 min-w-0 border border-white/10"
  >
    {icon}
    <span dir="ltr" className="flex flex-col leading-tight min-w-0" style={{ fontFamily: BADGE_FONT }}>
      <span className="text-[8px] text-white/85 truncate">{eyebrow}</span>
      <span className="text-[12.5px] font-semibold text-white truncate -mt-0.5">{title}</span>
    </span>
  </a>
);

// Floating "download the app" strip pinned to the bottom of the home map —
// dismissible, and stays dismissed (localStorage) once closed so it doesn't
// nag a returning visitor every session. Portaled straight to <body> for
// good measure, but the actual fix is the z-[1000]: Leaflet's own panes
// (tile/overlay/marker/popup) use root-level z-index values up to 700 —
// .leaflet-container is `position: relative` with no z-index of its own,
// so it never isolates a stacking context for them, and they compete
// directly against page-level z-index. Anything below ~700 loses to them
// once tiles are loaded, regardless of DOM position.
export default function DownloadAppBanner({ language = 'ar' }) {
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    setDismissed(localStorage.getItem(DISMISS_KEY) === '1');
  }, []);

  if (dismissed) return null;

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  return createPortal(
    <div className="fixed bottom-0 inset-x-0 z-[1000] px-3 pb-3 md:px-4 md:pb-4 pointer-events-none">
      <div
        dir={language === 'ar' ? 'rtl' : 'ltr'}
        className="mx-auto max-w-xl bg-gradient-to-br from-primary to-primary-dark text-white rounded-2xl shadow-2xl border-t-2 border-gold ring-1 ring-black/5 px-4 py-3.5 pointer-events-auto"
      >
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-11 h-11 rounded-xl bg-white/15 flex-shrink-0">
            <PhoneGlyph />
          </div>
          <div className={`flex-1 min-w-0 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
            <p className="text-sm font-bold leading-tight truncate">
              {language === 'ar' ? 'حمّل تطبيق بحرية' : 'Get the Ba7ria app'}
            </p>
            <p className="text-xs text-white/75 leading-tight truncate mt-0.5">
              {language === 'ar' ? 'للحصول على أفضل تجربة' : 'For the best experience'}
            </p>
          </div>
          <button
            onClick={handleDismiss}
            aria-label={language === 'ar' ? 'إغلاق' : 'Close'}
            className="flex-shrink-0 p-1.5 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
          >
            <CloseIcon className="w-4 h-4" />
          </button>
        </div>
        <div className="flex items-stretch gap-2 mt-3">
          <StoreBadge href={PLAY_STORE_URL} icon={<PlayGlyph />} eyebrow="GET IT ON" title="Google Play" />
          <StoreBadge href={APP_STORE_URL} icon={<AppleGlyph />} eyebrow="Download on the" title="App Store" />
        </div>
      </div>
    </div>,
    document.body
  );
}
